// lib/services/capture/camera_info.ts

import { YuvFormat } from "../compute/yuvdefs"
import type { CameraDiagnostics } from "./camera_diagnostics"

export type CameraFacing = "front" | "back" | "unknown";

export default class CameraInfo {
  device_id: string = "";
  label: string = "";
  facing: CameraFacing = "unknown";

  format: YuvFormat = "NV12";

  width: number = 0;
  height: number = 0;
  framerate: number = 0;

  y_width: number = 0;
  y_height: number = 0;
  chroma_width: number = 0;
  chroma_height: number = 0;

  probe: {
    format: YuvFormat;
    coded_width: number;
    coded_height: number;
    display_width: number;
    display_height: number;
  } | null = null;

  // WebRTC settings/capabilities snapshot + wide-angle heuristic, captured
  // during probe_capabilities. Diagnostics only; never drives selection.
  diagnostics: CameraDiagnostics | null = null;

  constructor() {}

  set_resolution(
    format: YuvFormat,
    width: number,
    height: number,
    y_width: number,
    y_height: number,
    chroma_width: number,
    chroma_height: number
  ): void {
    this.format = format;
    this.width = width;
    this.height = height;
    this.y_width = y_width;
    this.y_height = y_height;
    this.chroma_width = chroma_width;
    this.chroma_height = chroma_height;
  }

  /**
   * Human-readable summary used by the camera diagnostics page.
   */
  get_selected_string(): string {
    if (!this.device_id) return "";
    const facing = this.facing === "unknown" ? "" : ` (${this.facing})`;
    const dims = this.width && this.height ? ` ${this.width}x${this.height}` : "";
    const fps = this.framerate ? ` @ ${Math.round(this.framerate)}fps` : "";
    const label = this.label || "Camera";
    return `${label}${facing}${dims}${fps}`;
  }

  /**
   * For UI tables — only the visible fields.
   */
  toJSON(): {
    label: string;
    facing: CameraFacing;
    framerate: number;
    width: number;
    height: number;
    device_id: string;
  } {
    return {
      label: this.label,
      facing: this.facing,
      framerate: this.framerate,
      width: this.width,
      height: this.height,
      device_id: this.device_id,
    };
  }
}